/**
 * WIZARD_CONFIG — declarative description of the cold-start tool's 5 steps.
 *
 * Same pattern as the questionnaire: a renderer iterates this config and a
 * single `DynamicField` switch handles every input kind. To add a new field,
 * append to the steps array — no React changes required.
 *
 * Field names are typed against `ProfileInput` so a typo trips the compiler.
 */
import type { ProfileInput, StepConfig, WizardConfig } from './types';

// ── Sensible defaults (a realistic ₹28L Hyderabad salaried profile) ─────────
//
// Used as initial form state. Every numeric field has a starting value so the
// wizard can be eyeballed end-to-end without typing anything in.
export const DEFAULT_PROFILE: ProfileInput = {
  age:                'below60',
  gross:              2800000,
  basic:              1120000,
  hasHra:             true,
  hraReceived:        560000,
  rentPaid:           480000,
  isMetro:            false,
  homeLoanInterest:   0,
  s80c:               150000,
  nps80ccd1b:         50000,
  s80dSelf:           25000,
  s80dSelfSenior:     false,
  s80dParents:        50000,
  s80dParentsSenior:  true,
  eduLoanInterest:    0,
  employerNpsPct:     10,
  mealVouchers:       13200,
  mobileReimb:        30000,
  uniformAllowance:   0,
  fdInterest:         0,
  savingsInterest:    0,
  stcgDebt:           0,
  ltcgEquity:         0,
  stcgEquity:         0,
  ltcgDebt:           0,
  ltcgRealEstate:     0,
};

// ── Step definitions ────────────────────────────────────────────────────────

const STEP_SALARY: StepConfig = {
  index: 0,
  eyebrow: 'Step 1 of 5 — Salary income',
  title:    'Tell us about your salary.',
  subtitle: 'We need gross salary (CTC) and basic pay separately — basic drives HRA, NPS, EPF computations.',
  fields: [
    {
      kind: 'amount',
      id:    'gross',
      name:  'gross',
      label: 'Annual gross salary (CTC)',
      hint:  'Total CTC including basic, HRA, allowances and variable pay.',
      step:  10000,
      liveCaption: (v) => (v >= 100000 ? '₹' + (v / 100000).toFixed(1).replace(/\.0$/, '') + 'L' : ''),
    },
    {
      kind: 'amount',
      id:    'basic',
      name:  'basic',
      label: 'Annual basic salary',
      hint:  'Typically 35–50% of CTC. Look at your salary structure or payslip.',
      step:  10000,
      liveCaption: (v, profile) => {
        if (!profile.gross || !v) return '';
        return Math.round((v / profile.gross) * 100) + '% of CTC';
      },
    },
    {
      kind: 'age_band',
      id:    'age',
      name:  'age',
      label: 'Your age band',
      options: [
        { value: 'below60', label: 'Below 60 years',    sub: 'Standard slab rates apply' },
        { value: '60to80',  label: '60 – 80 years',     sub: 'Old regime nil slab up to ₹3L', badge: { text: 'Senior',       tone: 'forest' } },
        { value: 'above80', label: 'Above 80 years',    sub: 'Old regime nil slab up to ₹5L', badge: { text: 'Super senior', tone: 'amber'  } },
      ],
    },
  ],
};

const STEP_HOUSING: StepConfig = {
  index: 1,
  eyebrow: 'Step 2 of 5 — Housing',
  title:    'Rent paid & home loan interest.',
  subtitle: 'HRA exemption is computed from your actual rent. Both deductions apply only in the old regime.',
  fields: [
    // Toggle: do you receive HRA + pay rent? Children render only when on.
    // Drives a UI-only boolean on the profile (`hasHra`) — when false, the
    // wizard zeroes hraReceived/rentPaid before computing.
    {
      kind: 'toggle',
      id:    'has-hra',
      name:  'hasHra',
      label: 'Do you receive HRA in salary and pay rent?',
      trueLabel:  'Yes',
      falseLabel: 'No',
      // Children rendered when on:
      children: [
        {
          kind: 'amount',
          id:    'hra-received',
          name:  'hraReceived',
          label: 'HRA component in your salary (annual)',
          hint:  'From your salary structure. Often 20–40% of basic.',
          step:  1000,
        },
        {
          kind: 'amount',
          id:    'rent-paid',
          name:  'rentPaid',
          label: 'Annual rent paid',
          hint:  'Total annual rent — actual amount you pay your landlord.',
          step:  1000,
          liveCaption: (v) => (v ? '₹' + Math.round(v / 12).toLocaleString('en-IN') + '/mo' : ''),
        },
        {
          kind: 'toggle',
          id:    'is-metro',
          name:  'isMetro',
          label: 'Is your rented home in a metro city?',
          trueLabel:  'Metro (8 cities)',
          falseLabel: 'Non-metro',
          hint: 'Metro = 50% of basic. Delhi, Mumbai, Chennai, Kolkata, Bengaluru, Hyderabad, Pune, Ahmedabad. The four newer cities are added by Rule 279 of the Draft IT Rules 2026 from FY 2026-27 — pending final notification.',
        },
      ],
    },
    {
      kind: 'amount',
      id:    'home-loan-int',
      name:  'homeLoanInterest',
      label: 'Home loan interest paid (self-occupied)',
      sectionRef: 'S.22(2)(a)',
      hint:  'Annual interest paid. Old regime only. Capped at ₹2L for self-occupied property.',
      suffix: 'capped at ₹2L',
      step:  1000,
      max:   200000,
    },
  ],
};

const STEP_DEDUCTIONS: StepConfig = {
  index: 2,
  eyebrow: 'Step 3 of 5 — Deductions',
  title:    'Investments & insurance — actual amounts.',
  subtitle: 'Real planning needs the rupee figures: ₹50K of EPF gets a different result than ₹1.5L of EPF + ELSS + LIC.',
  fields: [
    {
      kind: 'amount',
      id:    's80c',
      name:  's80c',
      label: 'Section 80C investments — EPF, PPF, ELSS, LIC, tuition',
      sectionRef: 'S.123',
      hint:   'Total annual investment across all 80C-eligible items combined.',
      suffix: 'capped at ₹1.5L',
      step:   1000,
      max:    150000,
    },
    {
      kind: 'amount',
      id:    'nps-self',
      name:  'nps80ccd1b',
      label: 'NPS Tier-1 own contribution — extra ₹50K',
      sectionRef: 'S.124(3)',
      hint:   'Over and above the ₹1.5L 80C cap. Old regime only.',
      suffix: 'capped at ₹50K',
      step:   1000,
      max:    50000,
    },
    {
      kind: 'amount',
      id:    's80d-self',
      name:  's80dSelf',
      label: 'Health insurance premium — self & family',
      sectionRef: 'S.126',
      hint:   'Cap is ₹50K if you (the policyholder) are 60+.',
      suffix: 'cap ₹25K',
      step:   1000,
    },
    {
      kind: 'toggle',
      id:    'parents-senior',
      name:  's80dParentsSenior',
      label: 'Are your parents senior citizens (60+)?',
      trueLabel:  'Yes',
      falseLabel: 'No',
    },
    {
      kind: 'amount',
      id:    's80d-parents',
      name:  's80dParents',
      label: 'Health insurance premium — parents',
      sectionRef: 'S.126',
      hint:   'Cap is ₹50K if parents are senior citizens, else ₹25K.',
      suffix: 'no cap on actual premium',
      step:   1000,
    },
    {
      kind: 'amount',
      id:    'edu-loan',
      name:  'eduLoanInterest',
      label: 'Education loan interest',
      sectionRef: 'S.129',
      hint:   'Annual interest paid. Old regime only.',
      suffix: 'no cap',
      step:   1000,
    },
  ],
};

const STEP_PERKS: StepConfig = {
  index: 3,
  eyebrow: 'Step 4 of 5 — CTC structure',
  title:    'Employer perks & NPS.',
  subtitle: 'These reduce taxable salary in BOTH regimes — including the new regime. Often the highest-leverage lever for new-regime employees.',
  fields: [
    {
      kind: 'percent',
      id:    'emp-nps-pct',
      name:  'employerNpsPct',
      label: 'Employer NPS contribution',
      sectionRef: 'S.124(1)',
      hint:   'Up to 10% of basic for private employers (14% for government). Both regimes.',
      suffix: 'of basic salary',
      step:   0.5,
      max:    14,
    },
    {
      kind: 'amount',
      id:    'meal',
      name:  'mealVouchers',
      label: 'Meal vouchers / Sodexo',
      sectionRef: 'S.17(2)(e)',
      suffix: '~₹13,200/yr typical',
      step:   100,
    },
    {
      kind: 'amount',
      id:    'mobile',
      name:  'mobileReimb',
      label: 'Mobile / laptop reimbursement',
      sectionRef: 'S.17(1)(e)',
      suffix: 'actual bills reimbursed',
      step:   500,
    },
    {
      kind: 'amount',
      id:    'uniform',
      name:  'uniformAllowance',
      label: 'Uniform / official duty allowance',
      step:  500,
    },
  ],
};

const STEP_OTHER: StepConfig = {
  index: 4,
  eyebrow: 'Step 5 of 5 — Other income',
  title:    'Capital gains, interest, rental income.',
  subtitle: 'For most salaried filers these are zero. If you trade equity, hold FDs, or sold property — fill them in.',
  fields: [
    {
      kind: 'amount',
      id:    'fd',
      name:  'fdInterest',
      label: 'Fixed deposit interest (annual)',
      hint:  'Adds to your taxable income at slab rates.',
      suffix: 'slab rate',
      step:  1000,
    },
    {
      kind: 'amount',
      id:    'savings',
      name:  'savingsInterest',
      label: 'Savings account interest (annual)',
      hint:  '80TTA up to ₹10K applies in old regime.',
      suffix: 'slab rate',
      step:  500,
    },
    {
      kind: 'amount',
      id:    'ltcg-eq',
      name:  'ltcgEquity',
      label: 'LTCG — listed equity / equity MF',
      sectionRef: 'S.112A',
      hint:  'Holding > 12 months. First ₹1.25L exempt; balance taxed at 12.5%.',
      suffix: '12.5% above ₹1.25L',
      step:  1000,
    },
    {
      kind: 'amount',
      id:    'stcg-eq',
      name:  'stcgEquity',
      label: 'STCG — listed equity / equity MF',
      sectionRef: 'S.111A',
      hint:  'Holding ≤ 12 months. Flat 20%.',
      suffix: '20% flat',
      step:  1000,
    },
    {
      kind: 'amount',
      id:    'ltcg-debt',
      name:  'ltcgDebt',
      label: 'LTCG — debt MF / bonds',
      sectionRef: 'S.112',
      hint:  'Sold post-23-Jul-2024.',
      suffix: '12.5% no indexation',
      step:  1000,
    },
    {
      kind: 'amount',
      id:    'ltcg-re',
      name:  'ltcgRealEstate',
      label: 'LTCG — real estate',
      sectionRef: 'S.112',
      hint:  'No-indexation case. Indexation alternative needs a separate cost-base input.',
      suffix: '12.5% no indexation',
      step:  10000,
    },
  ],
};

export const WIZARD_CONFIG: WizardConfig = {
  steps: [STEP_SALARY, STEP_HOUSING, STEP_DEDUCTIONS, STEP_PERKS, STEP_OTHER],
};
